import React, { useState, useEffect } from "react";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { useTaskContext } from "../context/TaskContext";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia";
import Avatar from "../components/Avatar";

const TaskBoard = ({
  selectedProject,
  projectLists,
  projectTasks,
  listColors,
  zoomLevel,
  openNewListModal,
  handleDeleteListClick,
  openTaskModal,
  openDetailModal,
  openAttachModal,
  visibleCols = {
    etiquetas: true,
    usuarios: true,
    comentarios: true,
    archivos: true,
  },
}) => {
  const { updateTask, deleteTask } = useTaskContext();
  const { formatDate } = useFunctionsPrevia();
  const [isDragging, setIsDragging] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [visibleLists, setVisibleLists] = useState({});
  const [orderOverride, setOrderOverride] = useState({});

  useEffect(() => {
    if (!Object.keys(orderOverride).length) return;
    setOrderOverride((prev) => {
      const next = { ...prev };
      projectTasks.forEach((t) => {
        if (typeof next[t.id] === "number" && t.order === next[t.id]) {
          delete next[t.id];
        }
      });
      return next;
    });
  }, [projectTasks, orderOverride]); // ← incluye orderOverride para quitar el warning

  useEffect(() => {
    const onDown = (e) => {
      if (!activeMenu) return;
      const inside = e.target.closest(".menu-container");
      if (!inside) setActiveMenu(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [activeMenu]);

  useEffect(() => {
    const newVisibleLists = projectLists.reduce((acc, lst) => {
      const tasksInList = projectTasks.filter((t) => t.status === lst.name);
      acc[lst.name] = tasksInList.length > 0; // o true si quieres siempre abierto
      return acc;
    }, {});
    setVisibleLists(newVisibleLists);
  }, [projectLists, projectTasks]);

  const getContainerWidth = () => "300px";

  const getColumnCount = () => {
    switch (zoomLevel) {
      case 1:
        return 3; // 👈 con zoom 1 solo habrá 3 columnas horizontalmente
      case 2:
        return 3; // opcional, puedes dejarlo en 2 si quieres que el zoom achique más
      case 3:
        return 2;
      case 4:
        return 1;
      default:
        return 3;
    }
  };

  const ms = (ts) =>
    (ts?.seconds ?? 0) * 1000 + Math.floor((ts?.nanoseconds ?? 0) / 1e6);

  const getOrder = (t) =>
    typeof orderOverride[t.id] === "number"
      ? orderOverride[t.id]
      : typeof t?.order === "number"
      ? t.order
      : ms(t?.createdAt);

  const byOrder = (a, b) => getOrder(a) - getOrder(b);

  const toggleListVisibility = (listName) => {
    setVisibleLists((prev) => ({
      ...prev,
      [listName]: !prev[listName],
    }));
  };

  const onDragStart = () => setIsDragging(true);
  const onDragEnd = async (result) => {
    const { draggableId, source, destination } = result;
    try {
      if (!destination) return;
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      )
        return;

      const isFastBoard = selectedProject?.tipo === "todas";
      const findList = (id) =>
        projectLists.find((l) => l.id === id) ||
        projectLists.find((l) => l.name === id);

      const sourceList = findList(source.droppableId);
      const destList = findList(destination.droppableId);
      if (!destList) return;

      const task = projectTasks.find((t) => t.id === draggableId);
      if (!task) return;

      // vecinos en la lista destino (excluye la arrastrada)
      const destTasks = projectTasks
        .filter((t) => t.status === destList.name && t.id !== draggableId)
        .sort(byOrder);

      const prev = destTasks[destination.index - 1] || null;
      const next = destTasks[destination.index] || null;

      let newOrder;
      if (!prev && !next) newOrder = Date.now(); // única en la lista
      else if (!prev) newOrder = getOrder(next) - 1; // al inicio
      else if (!next) newOrder = getOrder(prev) + 1; // al final
      else newOrder = (getOrder(prev) + getOrder(next)) / 2;

      // ✅ UI inmediata
      setOrderOverride((ov) => ({ ...ov, [draggableId]: newOrder }));

      // persistencia
      if (isFastBoard) {
        await updateTask(
          draggableId,
          {
            ...task,
            isFast: true,
            prevListId: sourceList?.id || task.listId,
            listId: destList.id,
            status: destList.name,
            order: newOrder,
          },
          "Tarea movida"
        );
      } else {
        await updateTask(
          draggableId,
          {
            ...task,
            isFast: false,
            status: destList.name,
            order: newOrder,
          },
          "Tarea movida"
        );
      }
    } finally {
      setIsDragging(false);
    }
  };

  const toggleMenu = (id) => {
    setActiveMenu((prev) => (prev === id ? null : id));
  };

  return (
    <section className="kanban__main">
      <div
        className="kanban__main-wrapper"
        style={{
          gridTemplateColumns: `repeat(${getColumnCount()}, 1fr)`,
        }}
      >
        <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
          {projectLists.map((lst) => {
            const status = lst.name;
            const tasksInList = projectTasks
              .filter((t) => t.status === status)
              .sort(byOrder);
            const statusCapitalized =
              status.charAt(0).toUpperCase() +
              status.slice(1).replace(/-/g, " ");
            return (
              <Droppable key={lst.id} droppableId={lst.id}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`${status.replace(
                      / /g,
                      "-"
                    )}-color card-wrapper ${isDragging ? "dragging" : ""}`}
                    style={{
                      flexBasis: getContainerWidth(),
                      maxWidth: getContainerWidth(),
                      backgroundColor: "#fff",
                      height: "auto",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                      alignItems: "stretch",
                      margin: "0 10px",
                      "--list-color": listColors[status] || "#6C5CE7",
                    }}
                  >
                    {/* HEADER DE LISTA */}
                    <div className="card-wrapper__header">
                      <div className="list-head-left" style={{ flex: 1 }}>
                        <span
                          className="list-dot"
                          style={{
                            backgroundColor: listColors[status] || "#6C5CE7",
                          }}
                        />
                        <span className="list-title">{statusCapitalized}</span>
                        <span className="list-count">{tasksInList.length}</span>
                      </div>

                      <div className="list-head-right">
                        <div className="menu-container">
                          <i
                            className="bi bi-three-dots"
                            onClick={() => toggleMenu(status)}
                          />
                          {activeMenu === status && (
                            <div className="menu-list">
                              <button
                                className="menu-button"
                                onClick={() =>
                                  openNewListModal({
                                    name: status,
                                    color: listColors[status],
                                  })
                                }
                              >
                                <i
                                  className="bi bi-pencil"
                                  style={{ marginRight: 6 }}
                                />
                                Editar
                              </button>
                              <button
                                className="menu-button deleteMenu-button"
                                onClick={() => handleDeleteListClick(status)}
                              >
                                <i
                                  className="bi bi-trash"
                                  style={{ marginRight: 6 }}
                                />
                                Eliminar
                              </button>
                            </div>
                          )}
                        </div>

                        <i
                          className={`bi ${
                            visibleLists[status]
                              ? "bi-chevron-down"
                              : "bi-chevron-up"
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => toggleListVisibility(status)}
                        ></i>

                        <button
                          className="add-btn"
                          onClick={() => openTaskModal(lst)}
                          style={{
                            backgroundColor: `${
                              listColors[status] || "#6C5CE7"
                            }1A`,
                            color: listColors[status] || "#6C5CE7",
                          }}
                          aria-label="Agregar tarea"
                        >
                          <i className="bi bi-plus" />
                        </button>
                      </div>
                    </div>

                    {/* ÚNICA LÍNEA */}
                    <div
                      className="list-underline"
                      style={{
                        backgroundColor: listColors[status] || "#6C5CE7",
                      }}
                    />

                    {/* TARJETAS */}
                    {visibleLists[status] ? (
                      tasksInList.length === 0 ? (
                        <p className="empty-text">No hay tareas disponibles</p>
                      ) : (
                        tasksInList.map((task, idx) => (
                          <Draggable
                            key={task.id}
                            draggableId={task.id}
                            index={idx}
                          >
                            {(provided, snapshot) => {
                              const commentsCount = task.comments
                                ? task.comments.length
                                : 0;
                              const filesCount = (task.attachments || [])
                                .length;
                              const participants =
                                task.participants && task.participants.length
                                  ? task.participants
                                  : task.taggedUsers && task.taggedUsers.length
                                  ? task.taggedUsers.map((u) => u.id) // compatibilidad con tareas viejas
                                  : [task.userId].filter(Boolean);

                              return (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  className={`task-card ${
                                    snapshot.isDragging ? "is-dragging" : ""
                                  }`}
                                  style={provided.draggableProps.style}
                                >
                                  <div className="task-top">
                                    <h4 className="task-title">{task.title}</h4>

                                    <div className="menu-container">
                                      <button
                                        className="kebab-btn"
                                        onClick={() => toggleMenu(task.id)}
                                        aria-label="Menú de tarjeta"
                                      >
                                        <i className="bi bi-three-dots" />
                                      </button>

                                      {activeMenu === task.id && (
                                        <div className="menu-list">
                                          <button
                                            className="menu-button"
                                            onClick={() => {
                                              openTaskModal(lst, task);
                                              toggleMenu(null);
                                            }}
                                          >
                                            <i
                                              className="bi bi-pencil"
                                              style={{ marginRight: 6 }}
                                            />
                                            Editar
                                          </button>
                                          <button
                                            className="menu-button deleteMenu-button"
                                            onClick={() => {
                                              deleteTask(task.id, {
                                                isFast: true,
                                                listId: lst.id,
                                              });
                                              toggleMenu(null);
                                            }}
                                          >
                                            <i
                                              className="bi bi-trash"
                                              style={{ marginRight: 6 }}
                                            />
                                            Eliminar
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <p className="task-desc">
                                    {task.note && (
                                      <p className="task-desc">{task.note}</p>
                                    )}
                                  </p>

                                  {visibleCols?.etiquetas &&
                                    task.tags?.length > 0 && (
                                      <div className="task-tags">
                                        {task.tags.slice(0, 3).map((tag, i) => (
                                          <span
                                            key={`${task.id}-tag-${i}`}
                                            className={`tag tag-${(i % 5) + 1}`}
                                          >
                                            {tag}
                                          </span>
                                        ))}
                                      </div>
                                    )}

                                  <div
                                    className="task-meta"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: visibleCols?.usuarios
                                        ? "space-between"
                                        : "flex-end", // ⬅️ NUEVO
                                    }}
                                  >
                                    {visibleCols?.usuarios && (
                                      <div className="avatars">
                                        {participants
                                          .slice(0, 3)
                                          .map((uid, i) => (
                                            <div
                                              className="avatar-wrap"
                                              key={uid || i}
                                              style={{ zIndex: 3 - i }}
                                            >
                                              <Avatar userId={uid} size={28} />
                                            </div>
                                          ))}
                                        {participants.length > 3 && (
                                          <span className="avatar-more">
                                            +{participants.length - 3}
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    <div className="meta-right">
                                      {visibleCols?.comentarios && (
                                        <button
                                          className="meta-item"
                                          onClick={() => openDetailModal(task)}
                                        >
                                          <i className="bi bi-chat-left-text" />
                                          <span>{commentsCount}</span>
                                        </button>
                                      )}
                                      {/* ARCHIVOS */}
                                      {visibleCols?.archivos && (
                                        <button
                                          className="meta-item"
                                          onClick={() => openAttachModal(task)}
                                        >
                                          <i className="bi bi-folder2" />
                                          <span>{filesCount}</span>
                                        </button>
                                      )}

                                      {task.dateStart && (
                                        <div
                                          className="meta-item"
                                          aria-label="Fecha"
                                        >
                                          <i className="bi bi-calendar3" />
                                          <span>
                                            {formatDate(task.dateStart)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            }}
                          </Draggable>
                        ))
                      )
                    ) : null}

                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            );
          })}
        </DragDropContext>
      </div>
    </section>
  );
};

export default TaskBoard;
